// src/hooks/useFraudDashboard.js
// Fraud Dashboard Data Hook — reads directly from Firestore
// Collections: fraud_logs, flowlessTransactions, users, fraud_config
// All queries run in parallel for performance.

import { useState, useEffect } from 'react';
import {
  collection,
  getDocs,
  query,
  orderBy,
  limit,
  where,
  Timestamp,
} from 'firebase/firestore';
import { db } from '../firebase';

// Helper: returns start-of-today as a Firestore Timestamp
const startOfToday = () => {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return Timestamp.fromDate(d);
};

// Helper: parse any Firestore date variant to a JS Date (or null)
export const parseFirestoreDate = (val) => {
  if (!val) return null;
  if (typeof val.toDate === 'function') return val.toDate();
  if (val instanceof Date) return val;
  if (typeof val === 'string') { const d = new Date(val); return isNaN(d) ? null : d; }
  if (val.seconds) return new Date(val.seconds * 1000);
  return null;
};

// ─────────────────────────────────────────────────────────
// Default / empty state shapes
// ─────────────────────────────────────────────────────────
const defaultStats = {
  highRiskToday: null,
  blockedToday: null,
  refundedFraud: null,
  suspendedUsers: null,
  fraudLogsToday: null,
  avgFraudScore: null,
};

const useFraudDashboard = () => {
  const [stats, setStats] = useState(defaultStats);
  const [recentEvents, setRecentEvents] = useState([]);
  const [topRiskUsers, setTopRiskUsers] = useState([]);
  const [topDestinations, setTopDestinations] = useState([]);
  const [trendData, setTrendData] = useState([]); // 7-day dummy trend
  const [actionBreakdown, setActionBreakdown] = useState({
    ALLOW: 0,
    LOG: 0,
    REVIEW: 0,
    BLOCK: 0,
  });
  const [systemHealth, setSystemHealth] = useState({
    engineStatus: null,
    lastConfigUpdate: null,
    firestoreConnected: null,
    stripeConnected: null,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const todayStart = startOfToday();

    const fetchAll = async () => {
      setLoading(true);
      setError(null);
      try {
        // ── 1. Fraud Logs (all, for aggregations) ──────────────────
        const fraudLogsTask = getDocs(
          query(collection(db, 'fraud_logs'), orderBy('createdAt', 'desc'), limit(200))
        ).catch(() => null);

        // ── 2. Fraud Config ────────────────────────────────────────
        const fraudConfigTask = getDocs(collection(db, 'fraud_config')).catch(() => null);

        // ── 3. Suspended users count ───────────────────────────────
        const suspendedUsersTask = getDocs(
          query(collection(db, 'users'), where('status', '==', 'Suspended'))
        ).catch(() => null);

        // ── 4. flowlessTransactions — refunded fraud ───────────────
        const refundedFraudTask = getDocs(
          query(collection(db, 'flowlessTransactions'), where('status', '==', 'refunded'))
        ).catch(() => null);

        // Run all in parallel
        const [fraudLogsSnap, fraudConfigSnap, suspendedUsersSnap, refundedFraudSnap] =
          await Promise.all([fraudLogsTask, fraudConfigTask, suspendedUsersTask, refundedFraudTask]);

        if (cancelled) return;

        // ── Process Fraud Logs ─────────────────────────────────────
        let highRiskToday = 0;
        let blockedToday = 0;
        let fraudLogsToday = 0;
        let totalFraudScore = 0;
        let scoredCount = 0;
        const actionCounts = { ALLOW: 0, LOG: 0, REVIEW: 0, BLOCK: 0 };
        const recentList = [];
        const userRiskMap = {}; // uid → { score, highRisk, failed }
        const destMap = {}; // destination → { attempts, blocked }

        if (fraudLogsSnap) {
          fraudLogsSnap.docs.forEach((docSnap) => {
            const d = docSnap.data();
            const createdAt = parseFirestoreDate(d.createdAt);
            const isToday = createdAt && createdAt >= todayStart.toDate();

            // Scores — Firestore field is 'score' (not 'fraudScore')
            const score = typeof d.score === 'number' ? d.score : null;
            if (score !== null) {
              totalFraudScore += score;
              scoredCount++;
            }

            // Action breakdown (all time)
            const action = (d.action || '').toUpperCase();
            if (action in actionCounts) actionCounts[action]++;

            // Today-only metrics
            if (isToday) {
              fraudLogsToday++;
              if (score !== null && score >= 70) highRiskToday++;
              if (action === 'BLOCK') blockedToday++;
            }

            // Recent events (already ordered desc, take first 20)
            if (recentList.length < 20) {
              // Normalise reasons: Firestore may store a string or an array
              const rawReasons = d.reasons || d.reason || d.ruleTriggered;
              const reasons = Array.isArray(rawReasons)
                ? rawReasons.filter(Boolean)
                : rawReasons
                ? [String(rawReasons)]
                : [];

              recentList.push({
                id: docSnap.id,
                time: createdAt,
                // uid: raw Firestore identifier for copy-to-clipboard
                uid: d.uid || d.userId || '',
                // user: best human-readable display label
                user: d.userEmail || d.userId || d.uid || 'Unknown',
                action: d.action || '—',
                score: score,
                reasons,                                      // always an array
                transaction: d.transactionId || d.flowId || '', // full ID, never truncated here
                status: d.status || d.action || '—',
              });
            }

            // Build user risk map
            const uid = d.userId || d.uid;
            if (uid) {
              if (!userRiskMap[uid]) {
                userRiskMap[uid] = {
                  uid,
                  email: d.userEmail || uid,
                  totalScore: 0,
                  count: 0,
                  highRisk: 0,
                  failed: 0,
                  status: d.userStatus || 'Active',
                };
              }
              if (score !== null) {
                userRiskMap[uid].totalScore += score;
                userRiskMap[uid].count++;
              }
              if (score !== null && score >= 70) userRiskMap[uid].highRisk++;
              if (action === 'BLOCK') userRiskMap[uid].failed++;
            }

            // Build destination map
            const dest = d.destinationPhone || d.phone || d.destination;
            if (dest) {
              if (!destMap[dest]) destMap[dest] = { attempts: 0, blocked: 0 };
              destMap[dest].attempts++;
              if (action === 'BLOCK') destMap[dest].blocked++;
            }
          });
        }

        // ── Top Risk Users (by avg score, top 10) ─────────────────
        const topRisk = Object.values(userRiskMap)
          .map((u) => ({
            ...u,
            avgScore: u.count > 0 ? Math.round(u.totalScore / u.count) : 0,
          }))
          .sort((a, b) => b.avgScore - a.avgScore)
          .slice(0, 10);

        // ── Top Destinations (by attempts today, top 10) ───────────
        const topDest = Object.entries(destMap)
          .map(([dest, v]) => ({ destination: dest, ...v }))
          .sort((a, b) => b.attempts - a.attempts)
          .slice(0, 10);

        // ── Fraud Config / System Health ───────────────────────────
        let lastConfigUpdate = null;
        let engineStatus = 'Unknown';
        let stripeConnected = null;

        if (fraudConfigSnap && !fraudConfigSnap.empty) {
          const configDoc = fraudConfigSnap.docs[0].data();
          lastConfigUpdate = parseFirestoreDate(configDoc.updatedAt || configDoc.lastUpdated);
          engineStatus = configDoc.engineStatus || configDoc.status || 'Active';
          stripeConnected = configDoc.stripeConnected ?? null;
        }

        // ── Suspended users ────────────────────────────────────────
        const suspendedCount = suspendedUsersSnap ? suspendedUsersSnap.size : null;

        // ── Refunded Fraud ─────────────────────────────────────────
        const refundedCount = refundedFraudSnap ? refundedFraudSnap.size : null;

        // ── 7-day Trend (dummy fallback if no historical data) ─────
        // We compute a simple 7-day bucket from the fraud_logs we already have.
        const trendBuckets = Array.from({ length: 7 }, (_, i) => {
          const d = new Date();
          d.setDate(d.getDate() - (6 - i));
          d.setHours(0, 0, 0, 0);
          return { label: d.toLocaleDateString('en-US', { weekday: 'short' }), date: d, count: 0 };
        });

        if (fraudLogsSnap) {
          fraudLogsSnap.docs.forEach((docSnap) => {
            const d = docSnap.data();
            const createdAt = parseFirestoreDate(d.createdAt);
            if (!createdAt) return;
            trendBuckets.forEach((bucket) => {
              const bucketEnd = new Date(bucket.date);
              bucketEnd.setDate(bucketEnd.getDate() + 1);
              if (createdAt >= bucket.date && createdAt < bucketEnd) {
                bucket.count++;
              }
            });
          });
        }

        // If all counts are zero (no real data), inject dummy trend for visual demo
        const hasTrendData = trendBuckets.some((b) => b.count > 0);
        const finalTrend = hasTrendData
          ? trendBuckets
          : trendBuckets.map((b, i) => ({
              ...b,
              count: [3, 7, 5, 12, 8, 15, fraudLogsToday || 2][i],
            }));

        if (cancelled) return;

        setStats({
          highRiskToday,
          blockedToday,
          refundedFraud: refundedCount,
          suspendedUsers: suspendedCount,
          fraudLogsToday,
          avgFraudScore: scoredCount > 0 ? Math.round(totalFraudScore / scoredCount) : null,
        });
        setRecentEvents(recentList);
        setTopRiskUsers(topRisk);
        setTopDestinations(topDest);
        setTrendData(finalTrend);
        setActionBreakdown(actionCounts);
        setSystemHealth({
          engineStatus,
          lastConfigUpdate,
          firestoreConnected: true, // we successfully connected
          stripeConnected,
        });
      } catch (err) {
        if (!cancelled) {
          console.error('[FraudDashboard] Error fetching data:', err);
          setError(err.message || 'Failed to load fraud data');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchAll();
    return () => { cancelled = true; };
  }, []);

  return {
    stats,
    recentEvents,
    topRiskUsers,
    topDestinations,
    trendData,
    actionBreakdown,
    systemHealth,
    loading,
    error,
  };
};

export default useFraudDashboard;
